import React, { useState, useEffect, useMemo } from "react";
import GenericForm, { FormFieldConfig } from "../../../../components/genericform/GenericForm";
import ImageUploader from "../../ImageUploader";
import { Staff, StaffDialogProps, StaffFormData } from "src/shared/services/interfacev2";
import { ToastContainer } from "react-toastify";

interface EditStaffDialogProps extends StaffDialogProps {
  staff: Staff | null;
  onUpdateStaff: (id: string, formData: StaffFormData) => Promise<boolean>;
}

const EditStaffDialog: React.FC<EditStaffDialogProps> = ({ open, onClose, staff, onUpdateStaff }) => {
  const initialState: StaffFormData = {
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    emails: "",
    image: null,
  };

  const [formData, setFormData] = useState<StaffFormData>(initialState);

  useEffect(() => {
    if (!staff) return;

    setFormData({
      firstName: staff.firstName || "",
      middleName: staff.middleName || "",
      lastName: staff.lastName || "",
      phoneNumber: staff.phones?.[0]?.phone || "", // backend phones array
      emails: staff.emails?.[0]?.email || "",
      image: null, // new image only
    });
  }, [staff]);

  const handlePhoneChange = (val: string) => {
    if (/^\d{0,8}$/.test(val)) {
      setFormData((prev) => ({ ...prev, phoneNumber: val }));
    }
  };

  const handleSubmit = async () => {
    if (!staff) return;

    const success = await onUpdateStaff(staff._id.$oid, formData);

    // ❌ Do NOT close on error
    if (!success) return;

    handleClose();
  };

  const handleClose = () => {
    setFormData(initialState);
    onClose();
  };

  const isFormValid = useMemo(() => {
    if (!staff) return false;

    const nameValid = formData.firstName.trim() !== "";
    const phoneValid = /^\d{8}$/.test(formData.phoneNumber);

    const changed =
      formData.firstName !== (staff.firstName || "") ||
      formData.middleName !== (staff.middleName || "") ||
      formData.lastName !== (staff.lastName || "") ||
      formData.phoneNumber !== (staff.phones?.[0] || "") ||
      formData.emails !== (staff.emails?.[0] || "") ||
      formData.image !== null;

    return nameValid && phoneValid && changed;
  }, [formData, staff]);

  const fields: FormFieldConfig[] = [
    {
      id: "firstName",
      label: "First Name",
      required: true,
      value: formData.firstName,
      onChange: (val: string) => setFormData((prev) => ({ ...prev, firstName: val })),
    },
    {
      id: "middleName",
      label: "Middle Name",
      value: formData.middleName,
      onChange: (val: string) => setFormData((prev) => ({ ...prev, middleName: val })),
    },
    {
      id: "lastName",
      label: "Last Name",
      value: formData.lastName,
      onChange: (val: string) => setFormData((prev) => ({ ...prev, lastName: val })),
    },
    {
      id: "phoneNumber",
      label: "Phone Number",
      type: "text",
      required: true,
      value: formData.phoneNumber,
      onChange: handlePhoneChange,
    },
    {
      id: "emails",
      label: "Email",
      type: "email",
      value: formData.emails,
      onChange: (val: string) => setFormData((prev) => ({ ...prev, emails: val })),
    },
  ];

  return (
    <GenericForm open={open} title="Edit Staff Details" fields={fields} onClose={handleClose} onSubmit={handleSubmit} isFormValid={() => isFormValid} confirmLabel="Confirm" cancelLabel="Cancel">
      <div className="staff-image-input mt-3">
        <ImageUploader value={formData.image} onChange={(file) => setFormData((prev) => ({ ...prev, image: file }))} />

        {!formData.image && staff?.staffImageKey && (
          <div className="mt-2">
            <p className="text-sm text-gray-600 mb-1">Current Image:</p>
            <img src={staff.staffImageKey} alt="Current staff" className="w-[50px] h-[50px] object-cover rounded border border-gray-300" />
          </div>
        )}
      </div>
      <ToastContainer />
    </GenericForm>
  );
};

export default EditStaffDialog;
